/**
 * SVG utility functions for pie/donut chart arc generation.
 */

export interface ArcPathOptions {
  cx: number;
  cy: number;
  outerRadius: number;
  innerRadius?: number;
  startAngle: number; // radians
  endAngle: number; // radians
}

/**
 * Generate an SVG arc path string for a pie/donut segment.
 * Angles are in radians; 0 = 12 o'clock, clockwise.
 */
export function arcPath(opts: ArcPathOptions): string {
  const { cx, cy, outerRadius, innerRadius = 0, startAngle, endAngle } = opts;
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;

  // Outer arc points
  const x1 = cx + outerRadius * Math.sin(startAngle);
  const y1 = cy - outerRadius * Math.cos(startAngle);
  const x2 = cx + outerRadius * Math.sin(endAngle);
  const y2 = cy - outerRadius * Math.cos(endAngle);

  if (innerRadius <= 0) {
    // Full pie wedge — line to center
    return [
      `M ${cx} ${cy}`,
      `L ${x1} ${y1}`,
      `A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x2} ${y2}`,
      'Z',
    ].join(' ');
  }

  // Donut segment — arc with inner hole
  const ix1 = cx + innerRadius * Math.sin(endAngle);
  const iy1 = cy - innerRadius * Math.cos(endAngle);
  const ix2 = cx + innerRadius * Math.sin(startAngle);
  const iy2 = cy - innerRadius * Math.cos(startAngle);

  return [
    `M ${x1} ${y1}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x2} ${y2}`,
    `L ${ix1} ${iy1}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${ix2} ${iy2}`,
    'Z',
  ].join(' ');
}

/**
 * Compute the midpoint angle of an arc (useful for label positioning).
 */
export function arcMidAngle(startAngle: number, endAngle: number): number {
  return (startAngle + endAngle) / 2;
}

/**
 * Get the (x, y) point at a given angle and radius from center.
 */
export function polarToCartesian(
  cx: number,
  cy: number,
  radius: number,
  angleRadians: number
): { x: number; y: number } {
  return {
    x: cx + radius * Math.sin(angleRadians),
    y: cy - radius * Math.cos(angleRadians),
  };
}
